import { Link, useSearchParams } from 'react-router-dom';
import { ChartLine, Link as LinkIcon, ShieldCheck } from '@phosphor-icons/react';
import { IdBadge } from '@/components/core/id-badge/id-badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';
import { fmtDate, fmtDateTime } from '../utils/report-format';

export type CampaignReportTab = 'performance' | 'links' | 'deliverability';

const TABS: Array<{
  key: CampaignReportTab;
  label: string;
  icon: typeof ChartLine;
  path: (id: string) => string;
}> = [
  {
    key: 'performance',
    label: 'Performance',
    icon: ChartLine,
    path: (id) => `/reports/campaigns/${id}`,
  },
  {
    key: 'links',
    label: 'Links & heatmap',
    icon: LinkIcon,
    path: (id) => `/reports/campaigns/${id}/links`,
  },
  {
    key: 'deliverability',
    label: 'Deliverability',
    icon: ShieldCheck,
    path: (id) => `/reports/campaigns/${id}/deliverability`,
  },
];

const STATUS_LABEL: Record<string, string> = {
  sent: 'Sent',
  sending: 'Sending',
  failed: 'Failed',
  draft: 'Draft',
  scheduled: 'Scheduled',
};

/** Prototype UIKit.pill tones (store-campaigns STATE_TONE). */
const STATUS_PILL: Record<string, { wrap: string; dot: string; pulse?: boolean }> = {
  sent: { wrap: 'bg-[#E7F4EC] text-[#27272C]', dot: 'bg-[#1F8A46]' },
  sending: {
    wrap: 'bg-[#E6F0F8] text-[#27272C]',
    dot: 'bg-[#0066B2]',
    pulse: true,
  },
  failed: { wrap: 'bg-[#FCE4EA] text-[#27272C]', dot: 'bg-[#D80032]' },
  draft: { wrap: 'bg-[#F4F5F6] text-[#27272C]', dot: 'bg-[#757575]' },
  scheduled: { wrap: 'bg-[#E6F0F8] text-[#27272C]', dot: 'bg-[#0066B2]' },
};

export type CampaignNavModel = {
  itemId: string;
  name: string;
  status: string;
  sentAt: string | null;
  lastEventAt: string | null;
};

export type CampaignNavOption = {
  itemId: string;
  name: string;
  status: string | null;
};

type CampaignReportNavProps = {
  campaign: CampaignNavModel;
  options: CampaignNavOption[];
  active: CampaignReportTab;
  onCampaignChange: (id: string) => void;
};

export function CampaignReportNav({
  campaign,
  options,
  active,
  onCampaignChange,
}: CampaignReportNavProps) {
  const [params] = useSearchParams();
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : '';
  const pickerOptions = options.some((c) => c.itemId === campaign.itemId)
    ? options
    : [campaign, ...options];

  const statusLabel = STATUS_LABEL[campaign.status] || campaign.status;
  const pill = STATUS_PILL[campaign.status] || STATUS_PILL.draft;

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm shrink-0">
      <div className="flex flex-wrap items-start gap-4 p-5 pb-4">
        <div className="min-w-[260px] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-neutral-900 sm:text-2xl">{campaign.name}</h1>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
                pill.wrap
              )}
            >
              <span
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  pill.dot,
                  pill.pulse && 'animate-pulse'
                )}
                aria-hidden
              />
              {statusLabel}
            </span>
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            {campaign.sentAt ? `Sent ${fmtDate(campaign.sentAt)}` : 'Not sent yet'}
            {campaign.lastEventAt ? ` · counts as of ${fmtDateTime(campaign.lastEventAt)}` : ''}
          </p>
          <div className="mt-2">
            <IdBadge id={campaign.itemId} label="Campaign ID" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium text-neutral-500" htmlFor="reportCampaignPicker">
            Viewing
          </label>
          <Select value={campaign.itemId} onValueChange={onCampaignChange}>
            <SelectTrigger
              id="reportCampaignPicker"
              aria-label="Switch campaign"
              className="h-9 w-auto min-w-[220px] max-w-[280px] border-neutral-300 bg-white px-2.5 text-[13px] text-neutral-900 shadow-none hover:border-neutral-500 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/30 focus:ring-offset-0"
            >
              <SelectValue placeholder="Select a campaign" />
            </SelectTrigger>
            <SelectContent align="end" className="min-w-[220px]">
              {pickerOptions.map((option) => (
                <SelectItem key={option.itemId} value={option.itemId} className="text-[13px]">
                  {option.name}
                  {option.status === 'sent' || !option.status
                    ? ''
                    : ` (${STATUS_LABEL[option.status] || option.status})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <nav
        className="mt-0.5 flex flex-wrap gap-0.5 border-t border-[#F0F1F2] px-5"
        aria-label="Campaign report views"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = t.key === active;
          return (
            <Link
              key={t.key}
              to={`${t.path(campaign.itemId)}${suffix}`}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'mb-[-1px] inline-flex items-center gap-1.5 border-b-2 border-transparent px-3.5 py-2.5 text-sm text-neutral-600 hover:bg-[#FAFAFB] hover:text-neutral-900',
                on && 'border-primary-600 font-semibold text-primary-600'
              )}
            >
              <Icon className="size-[15px]" aria-hidden />
              {t.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
